import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import * as XLSX from 'xlsx';
import NotificationPopup from '../component/NotificationPopup';
import InterviewFilters from './InterviewFilters';
import InterviewTable from './InterviewTable';
import ViewResult from '../component/ViewResult';
import EmailConfirmModal from './EmailConfirmModal';
import useInterviewData from './useInterviewData';
import useInterviewResults from './useInterviewResults';
import { STATUS_OPTIONS, formatDate, getInterviewRowId } from './interviewUtils';
import '../admin.css';
import Pagination from '../component/Pagination';
import api from '../../../api/client';
import JDDetailsModal from '../component/JDDetailsModal';
import Timeline from '../component/Timeline';
import { CommentsModal } from '../../comments';
import DynamicBasicDialog from '../component/DynamicBasicDialog';
import FeedbackModal from './FeedbackModal';

export default function InterviewOverview({ onLogout }) {
    const { t } = useTranslation();
    const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);
    const [isCompanyAdmin, setIsCompanyAdmin] = React.useState(false);
    const [jdDialogOpen, setJdDialogOpen] = React.useState(false);
    const [jdDialogData, setJdDialogData] = React.useState(null);
    const [timelineOpen, setTimelineOpen] = React.useState(false);
    const [timelineSessionId, setTimelineSessionId] = React.useState(null);
    const [timelineCandidateInfo, setTimelineCandidateInfo] = React.useState(null);
    const [commentsModalOpen, setCommentsModalOpen] = React.useState(false);
    const [selectedSessionForComments, setSelectedSessionForComments] = React.useState(null);
    const [selectedSessionData, setSelectedSessionData] = React.useState(null);
    const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);
    const [sessionToArchive, setSessionToArchive] = React.useState(null);
    const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
    const [feedbackModalData, setFeedbackModalData] = React.useState(null);
    const [feedbackModalLoading, setFeedbackModalLoading] = React.useState(false);
    const [feedbackModalError, setFeedbackModalError] = React.useState('');
    const [feedbackCandidateInfo, setFeedbackCandidateInfo] = React.useState(null);
    const {
        // Data
        candidates,
        loading,
        error,
        paginationData,
        dropdownOptions,

        // Filter state
        filters,
        statusCounts,

        // Filter actions
        updateFilter,
        clearAllFilters,
        hasActiveFilters,

        // Pagination actions
        pageSize,
        updatePage,
        updatePageSize,

        handleSort,
        getSortDirection,
        // See All & Infinite Virtualization
        isSeeAll,
        hasMore,
        isLoadingMore,
        fetchNextBatch,
        // Utilities
        refetch
    } = useInterviewData();

    const contentContainerRef = React.useRef(null);

    // Generate page size options including dynamic "See All"
    const pageSizeOptions = React.useMemo(() => {
        const total = paginationData?.total_count || 0;
        const base = [12, 25, 50, 100];
        const opts = base.map(size => ({ value: size, label: `${size}` }));
        if (total > 12) {
            if (!base.includes(total)) {
                opts.push({ value: total, label: `See All (${total})` });
            } else {
                const idx = opts.findIndex(o => o.value === total);
                if (idx !== -1) {
                    opts[idx] = { value: total, label: `${total} (See All)` };
                }
            }
        }
        if (pageSize && !opts.some(o => o.value === pageSize)) {
            opts.push({ value: pageSize, label: `See All (${pageSize})` });
        }
        return opts;
    }, [paginationData?.total_count, pageSize]);

    // Persist current page to localStorage when pagination changes
    React.useEffect(() => {
        try {
            if (paginationData && paginationData.page) {
                localStorage.setItem('interviewOverview_page', paginationData.page);
            }
        } catch (e) { }
    }, [paginationData]);

    const handlePageChange = (page) => {
        updatePage(page);
        try {
            localStorage.setItem('interviewOverview_page', page);
        } catch (e) { }
    };

    React.useEffect(() => {
        const checkCompanyAdminStatus = async () => {
            try {
                const response = await api.get('admin/company-admin-check/', {
                    headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
                });
                setIsCompanyAdmin(response.data.is_company_admin || false);
            } catch (err) {
                setIsCompanyAdmin(false);
            }
        };
        checkCompanyAdminStatus();
    }, []);

    const {
        showFeedbackModal,
        feedback,
        loadingFeedback,
        feedbackError,
        sessionData,
        isSendingEmail,
        emailSent,
        showEmailConfirmModal,
        emailRecipient,
        ccRecipients,

        setCcRecipients,
        setEmailRecipient,

        fetchInterviewResults,
        sendFeedbackEmail,

        openFeedbackModal,
        closeFeedbackModal,
        prepareEmailSend,
        closeEmailModal,
        resetAllStates
    } = useInterviewResults();

    const [snackbar, setSnackbar] = React.useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleClose = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleOpenTimeline = React.useCallback((sessionId, candidateInfo = null) => {
        if (!sessionId) return;
        setTimelineSessionId(sessionId);
        setTimelineCandidateInfo(candidateInfo);
        setTimelineOpen(true);
    }, []);

    const handleOpenComments = React.useCallback((sessionId, sessionData) => {
        setSelectedSessionForComments(sessionId);
        setSelectedSessionData(sessionData);
        setCommentsModalOpen(true);
    }, []);

    const handleArchiveClick = React.useCallback((candidate) => {
        setSessionToArchive(candidate);
        setArchiveDialogOpen(true);
    }, []);

    const handleConfirmArchive = async () => {
        if (!sessionToArchive || !sessionToArchive.session_id) {
            setSnackbar({
                open: true,
                message: t('interviewOverview.noSessionId'),
                severity: 'error'
            });
            setArchiveDialogOpen(false);
            setSessionToArchive(null);
            return;
        }

        const isArchived = sessionToArchive.is_archived;
        const endpoint = isArchived
            ? `sessions/${sessionToArchive.session_id}/unarchive/`
            : `sessions/${sessionToArchive.session_id}/archive/`;

        try {
            await api.post(endpoint);
            setSnackbar({
                open: true,
                message: isArchived
                    ? t('interviewOverview.interviewUnarchived', 'Interview unarchived successfully')
                    : t('interviewOverview.interviewArchived', 'Interview archived successfully'),
                severity: 'success'
            });
            setArchiveDialogOpen(false);
            setSessionToArchive(null);
            // Refetch the data to update the table
            if (typeof refetch === 'function') {
                refetch();
            }
        } catch (err) {
            setSnackbar({
                open: true,
                message: isArchived
                    ? t('interviewOverview.failedToUnarchive', 'Failed to unarchive interview')
                    : t('interviewOverview.failedToArchive', 'Failed to archive interview'),
                severity: 'error'
            });
            console.error(`Failed to ${isArchived ? 'unarchive' : 'archive'} interview:`, err);
        }
    };

    const handleOpenJDDetails = React.useCallback(async (jobDescriptionId) => {
        if (!jobDescriptionId) return;
        try {
            const res = await api.get(`job-descriptions/${jobDescriptionId}/`);
            setJdDialogData(res.data);
            setJdDialogOpen(true);
        } catch (e) {
            setJdDialogData({ title: t('interviewOverview.jobDescription'), description: t('interviewOverview.failedToLoadJD') });
            setJdDialogOpen(true);
        }
    }, [t]);

    const handleViewFeedback = React.useCallback(async (sessionId, candidate) => {
        if (!sessionId) return;
        setFeedbackCandidateInfo(candidate);
        setFeedbackModalOpen(true);
        setFeedbackModalLoading(true);
        setFeedbackModalError('');
        setFeedbackModalData(null);
        try {
            const res = await api.get(`feedback-questionnaires/responses/${sessionId}/`);
            setFeedbackModalData(res.data || null);
        } catch (err) {
            console.error('Failed to fetch questionnaire feedback:', err);
            setFeedbackModalError(
                t('feedback.fetchError', 'Failed to load feedback. Please try again.')
            );
        } finally {
            setFeedbackModalLoading(false);
        }
    }, [t]);

    const handleViewResult = React.useCallback(async (candidate) => {
        const sessionId = (typeof candidate === 'object' && candidate !== null)
            ? (candidate.session_id || candidate.id)
            : candidate;

        if (!sessionId) {
            setSnackbar({
                open: true,
                message: t('interviewOverview.noSessionId'),
                severity: 'error'
            });
            return;
        }
        openFeedbackModal();
        const result = await fetchInterviewResults(sessionId);
        if (!result.success) {
            setSnackbar({
                open: true,
                message: result.error,
                severity: 'error'
            });
        }
    }, [fetchInterviewResults, openFeedbackModal, t]);

    const sendEmail = async (feedback, sessionId, candidateEmail, candidateName, htmlContent = null, filename = null) => {
        prepareEmailSend(candidateName, candidateEmail, htmlContent, filename);
    };

    const handleConfirmEmail = async () => {
        if (!emailRecipient || !feedback) return;

        const emailData = {
            sessionId: feedback?.session,
            candidateEmail: emailRecipient.email,
            candidateName: emailRecipient.name,
            ccRecipients: ccRecipients.split(',').map(email => email.trim()).filter(email => email),
            html_content: emailRecipient.htmlContent,
            filename: emailRecipient.filename,
            feedback: feedback
        };

        const result = await sendFeedbackEmail(emailData);

        if (result.success) {
            setSnackbar({
                open: true,
                message: result.message,
                severity: 'success'
            });

            setTimeout(() => {
                resetAllStates();
            }, 2000);
        } else {
            setSnackbar({
                open: true,
                message: result.error,
                severity: 'error'
            });
        }
    };

    const [selectedSessionIds, setSelectedSessionIds] = React.useState([]);

    // Clear selection when filters change
    React.useEffect(() => {
        setSelectedSessionIds([]);
    }, [filters]);

    const handleToggleSelect = React.useCallback((sessionId) => {
        const s = String(sessionId);
        setSelectedSessionIds(prev => {
            return prev.some(id => String(id) === s)
                ? prev.filter(id => String(id) !== s)
                : [...prev, sessionId];
        });
    }, []);

    const selectedSet = React.useMemo(() => {
        return new Set(selectedSessionIds.map(String));
    }, [selectedSessionIds]);

    const handleTagsUpdated = React.useCallback(() => {
        if (typeof refetch === 'function') refetch();
    }, [refetch]);

    const handleSelectAll = React.useCallback((selectAll) => {
        const currentPageIds = (candidates || [])
            .map(getInterviewRowId)
            .filter(Boolean);

        setSelectedSessionIds(prev => {
            if (selectAll) {
                const prevSet = new Set(prev.map(String));
                const newItems = currentPageIds.filter(id => !prevSet.has(String(id)));
                return [...prev, ...newItems];
            } else {
                const toRemove = new Set(currentPageIds.map(String));
                return prev.filter(id => !toRemove.has(String(id)));
            }
        });
    }, [candidates]);

    const [isExporting, setIsExporting] = React.useState(false);

    const handleExport = React.useCallback(async (format = 'xlsx') => {
        const total = paginationData?.total_count || 0;
        if (total === 0) {
            setSnackbar({
                open: true,
                message: t('interviewOverview.noDataToExport', 'No interview data to export'),
                severity: 'warning'
            });
            return;
        }

        setIsExporting(true);
        try {
            let dataToExport = [];

            if (selectedSessionIds.length > 0) {
                const selectedSet = new Set(selectedSessionIds.map(String));
                // Check if all selected records are present in current candidates list
                const currentSelected = (candidates || []).filter(c => selectedSet.has(String(getInterviewRowId(c))));
                if (currentSelected.length === selectedSessionIds.length) {
                    dataToExport = currentSelected;
                } else {
                    // Fetch all matching records for the current active filters to find selected ones across other pages
                    const params = new URLSearchParams({
                        page: '1',
                        page_size: String(Math.min(total, 2000))
                    });
                    Object.entries(filters).forEach(([key, value]) => {
                        if (value && value.toString().trim()) {
                            params.append(key, value.toString().trim());
                        }
                    });
                    const response = await api.get(`interview-candidates/?${params.toString()}`, {
                        headers: {
                            'Authorization': `Token ${localStorage.getItem('adminToken')}`,
                        },
                    });
                    const allMatching = response.data?.candidates || [];
                    const candidateMap = new Map();
                    allMatching.forEach(c => candidateMap.set(String(getInterviewRowId(c)), c));
                    currentSelected.forEach(c => candidateMap.set(String(getInterviewRowId(c)), c));
                    dataToExport = selectedSessionIds
                        .map(id => candidateMap.get(String(id)))
                        .filter(Boolean);
                }
            } else {
                // If no specific checkboxes are selected, export all matching records for active filters
                if (candidates.length >= total) {
                    dataToExport = candidates;
                } else {
                    const params = new URLSearchParams({
                        page: '1',
                        page_size: String(Math.min(total, 2000))
                    });
                    Object.entries(filters).forEach(([key, value]) => {
                        if (value && value.toString().trim()) {
                            params.append(key, value.toString().trim());
                        }
                    });
                    const response = await api.get(`interview-candidates/?${params.toString()}`, {
                        headers: {
                            'Authorization': `Token ${localStorage.getItem('adminToken')}`,
                        },
                    });
                    dataToExport = response.data?.candidates || candidates;
                }
            }

            if (!dataToExport || dataToExport.length === 0) {
                setSnackbar({
                    open: true,
                    message: t('interviewOverview.noDataToExport', 'No interview data to export'),
                    severity: 'warning'
                });
                return;
            }

            const headers = [
                "Candidate Name",
                "Candidate Email",
                "Position / Job Title",
                "Department",
                "Experience Level",
                "Scheduled By",
                "Interview Date",
                "Language",
                "Status",
                "Tags",
                "Effective Score (%)",
                "Total Score (%)",
                "Resume URL",
                "Feedback Report Link"
            ];

            const rawRows = dataToExport.map((c) => {
                const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.username || '—';
                const email = c.email || '—';
                const jobTitle = c.job_title || '—';
                const department = c.department_name || '—';
                const experienceLevel = c.experience_level || '—';
                const scheduledBy = c.scheduled_by_name || '—';
                const interviewDate = c.ended_at ? formatDate(c.ended_at) : (c.started_at ? formatDate(c.started_at) : '—');
                const language = c.language_detail?.name || c.language_name || 'English';
                const status = c.status || '—';
                const tags = Array.isArray(c.tags) && c.tags.length > 0 ? c.tags.join(', ') : '—';
                const effectiveScore = (c.effective_score !== null && c.effective_score !== undefined) ? `${Number(c.effective_score).toFixed(2)}%` : '—';
                const totalScore = (c.total_score !== null && c.total_score !== undefined) ? `${Number(c.total_score).toFixed(2)}%` : '—';
                const resumeUrl = c.resume_url || '—';

                // Feedback Report Link
                let feedbackLink = '—';
                const companyId = c.company_id || c.company?.id;
                const candidateId = c.candidate_id || c.candidate?.id;
                const interviewId = c.session_id;
                if (c.status === 'completed' && companyId && candidateId && interviewId) {
                    feedbackLink = `${window.location.origin}/feedbacks/${companyId}/${candidateId}/${interviewId}`;
                }

                return [
                    fullName,
                    email,
                    jobTitle,
                    department,
                    experienceLevel,
                    scheduledBy,
                    interviewDate,
                    language,
                    status,
                    tags,
                    effectiveScore,
                    totalScore,
                    resumeUrl,
                    feedbackLink
                ];
            });

            const statusSuffix = filters.status ? `_${filters.status}` : '_all';
            const dateStr = new Date().toISOString().split('T')[0];

            if (format === 'csv') {
                const escapeCSV = (val) => {
                    if (val === null || val === undefined) return '""';
                    return `"${String(val).replace(/"/g, '""')}"`;
                };
                const csvRows = rawRows.map(r => r.map(escapeCSV).join(','));
                const csvContent = "\uFEFF" + headers.map(escapeCSV).join(',') + "\r\n" + csvRows.join("\r\n");
                try {
                    if (navigator?.clipboard?.writeText) {
                        navigator.clipboard.writeText(csvContent);
                    }
                } catch (e) { }

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', `interviews_overview${statusSuffix}_${dateStr}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                // Wide column widths: email, resume url, feedback report link extra wide + other columns comfortable
                const defaultColWidths = [
                    24, // Candidate Name
                    35, // Candidate Email (extra wide)
                    30, // Position / Job Title
                    24, // Department
                    18, // Experience Level
                    22, // Scheduled By
                    22, // Interview Date
                    16, // Language
                    16, // Status
                    28, // Tags
                    20, // Effective Score (%)
                    18, // Total Score (%)
                    55, // Resume URL (extra wide)
                    75  // Feedback Report Link (extra wide)
                ];

                const dynamicColWidths = headers.map((h, i) => {
                    let maxLen = h.length;
                    rawRows.forEach(r => {
                        const val = r[i];
                        if (val) {
                            const l = String(val).length;
                            if (l > maxLen) maxLen = l;
                        }
                    });
                    const baseMin = defaultColWidths[i] || 20;
                    return { wch: Math.min(Math.max(maxLen + 4, baseMin), 90) };
                });

                const ws = XLSX.utils.aoa_to_sheet([headers, ...rawRows]);
                ws['!cols'] = dynamicColWidths;
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Interviews');
                XLSX.writeFile(wb, `interviews_overview${statusSuffix}_${dateStr}.xlsx`);
            }

            setSnackbar({
                open: true,
                message: format === 'csv'
                    ? t('interviewOverview.csvCopiedSuccess', {
                        count: dataToExport.length,
                        defaultValue: `Copied ${dataToExport.length} interviews to CSV!`
                    })
                    : t('interviewOverview.exportSuccess', {
                        count: dataToExport.length,
                        format: 'Excel',
                        defaultValue: `Successfully exported ${dataToExport.length} interviews to Excel`
                    }),
                severity: 'success'
            });
        } catch (err) {
            console.error('Error exporting interviews:', err);
            setSnackbar({
                open: true,
                message: t('interviewOverview.exportFailed', 'Failed to export data. Please try again.'),
                severity: 'error'
            });
        } finally {
            setIsExporting(false);
        }
    }, [paginationData?.total_count, selectedSessionIds, candidates, filters, formatDate, t]);

    const handleExportCSV = React.useCallback(() => {
        handleExport('csv');
    }, [handleExport]);

    React.useEffect(() => {
        if (error) {
            setSnackbar({
                open: true,
                message: error,
                severity: 'error'
            });
        }
    }, [error]);

    return (
        <div className="h-[calc(100vh-40px)] overflow-hidden" style={{ paddingTop: '10px' }}>
            <NotificationPopup
                open={snackbar.open}
                onClose={handleClose}
                severity={snackbar.severity}
                message={snackbar.message}
            />

            <div className="max-w-8xl mx-auto p-3 sm:p-4 h-full">
                {/* NOTE: overflow-visible so sticky pagination isn't clipped, but allow horizontal scroll for table */}
                <div className="bg-white rounded-xl flex flex-col h-full overflow-hidden">
                    {/* Header with Gradient */}
                    <div className="rounded-t-xl px-4 py-3 text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h1 className="mb-0.5 text-lg font-semibold text-white">{t('candidate.interviewOverview')}</h1>
                                <p className="text-xs text-blue-100">{t('candidate.pageSubtitle')}</p>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-blue-100">{t('candidate.completedInterviews')}</div>
                                <div className="text-sm font-semibold text-white">
                                    {statusCounts?.completed ?? (candidates.filter(c => c.status === 'completed').length || 0)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <InterviewFilters
                        filters={filters}
                        statusCounts={statusCounts}
                        candidates={candidates}
                        onFilterChange={updateFilter}
                        onClearFilters={clearAllFilters}
                        hasActiveFilters={hasActiveFilters()}
                        showAdvancedFilters={showAdvancedFilters}
                        setShowAdvancedFilters={setShowAdvancedFilters}
                        companies={dropdownOptions.companies}
                        departments={dropdownOptions.departments}
                        companyAdmins={dropdownOptions.companyAdmins}
                        jobDescriptions={dropdownOptions.jobDescriptions}
                        statusOptions={STATUS_OPTIONS}
                        onExport={handleExport}
                        onExportCSV={handleExportCSV}
                        isExporting={isExporting}
                        totalCount={paginationData?.total_count || 0}
                        selectedCount={selectedSessionIds.length}
                    />

                    {/* Content - add bottom padding so sticky footer doesn't overlap last rows */}
                    <div
                        ref={contentContainerRef}
                        className="p-6 flex-1 overflow-auto [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <CircularProgress size={32} sx={{ color: '#2563eb' }} />
                            </div>
                        ) : candidates.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <div className="mx-auto mb-4 w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                    <Eye size={24} className="opacity-50" />
                                </div>
                                <p className="text-lg">{t('candidate.noInterviews')}</p>
                                <p className="">
                                    {hasActiveFilters() ? t('interviewOverview.adjustFilters') : t('candidate.noScheduledInterviews')}
                                </p>
                                {hasActiveFilters() && (
                                    <button
                                        onClick={clearAllFilters}
                                        className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        {t('interviewOverview.clearAllFilters')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <InterviewTable
                                candidates={candidates}
                                onViewResult={handleViewResult}
                                formatDate={formatDate}
                                onSort={handleSort}
                                getSortDirection={getSortDirection}
                                isCompanyAdmin={isCompanyAdmin}
                                onOpenJDDetails={handleOpenJDDetails}
                                companies={dropdownOptions.companies}
                                onOpenTimeline={handleOpenTimeline}
                                onOpenComments={handleOpenComments}
                                onArchive={handleArchiveClick}
                                onViewFeedback={handleViewFeedback}
                                onTagsUpdated={handleTagsUpdated}
                                currentPage={paginationData.page}
                                selectedSessionIds={selectedSessionIds}
                                selectedSet={selectedSet}
                                onToggleSelect={handleToggleSelect}
                                onSelectAll={handleSelectAll}
                                isSeeAll={isSeeAll}
                                onLoadMore={fetchNextBatch}
                                hasMore={hasMore}
                                isLoadingMore={isLoadingMore}
                                scrollContainerRef={contentContainerRef}
                            />
                        )}
                    </div>

                    {/* Pagination - fixed to bottom of viewport */}
                    <Pagination
                        currentPage={paginationData.page}
                        totalPages={paginationData.total_pages}
                        totalCount={paginationData.total_count}
                        currentCount={candidates.length}
                        onPageChange={handlePageChange}
                        onPageSizeChange={updatePageSize}
                        pageSize={pageSize}
                        showPageSize={true}
                        pageSizeOptions={pageSizeOptions}
                        hasActiveFilters={hasActiveFilters()}
                        isSeeAll={isSeeAll}
                        fixed={false}
                        forceVisible={true}
                        className="py-0"
                    />
                </div>
            </div>

            {/* ViewResult Modal */}
            {showFeedbackModal && (
                <ViewResult
                    feedback={feedback}
                    loadingFeedback={loadingFeedback}
                    feedbackError={feedbackError}
                    onClose={closeFeedbackModal}
                    sessionData={sessionData}
                    isSendingEmail={isSendingEmail}
                    emailSent={emailSent}
                    sendEmail={sendEmail}
                    callFrom="interviewOverview"
                />
            )}

            {/* Email Confirmation Modal */}
            <EmailConfirmModal
                isOpen={showEmailConfirmModal}
                onClose={closeEmailModal}
                onConfirm={handleConfirmEmail}
                emailRecipient={emailRecipient}
                setEmailRecipient={setEmailRecipient}
                ccRecipients={ccRecipients}
                setCcRecipients={setCcRecipients}
                isSendingEmail={isSendingEmail}
                emailSent={emailSent}
            />

            {/* JD Details Modal */}
            <JDDetailsModal
                open={jdDialogOpen}
                onClose={() => setJdDialogOpen(false)}
                jd={jdDialogData}
            />

            {/* Timeline Drawer */}
            <Timeline
                open={timelineOpen}
                onClose={() => {
                    setTimelineOpen(false);
                    setTimelineCandidateInfo(null);
                }}
                apiEndpoint={timelineSessionId ? `audit/timeline/interview/${timelineSessionId}` : ''}
                title={t('interviewOverview.timelineTitle', { defaultValue: 'Timeline' })}
                pageName="Interview Overview"
                width={480}
                candidateInfo={timelineCandidateInfo}
            />

            {/* Comments Modal */}
            {selectedSessionForComments && (
                <CommentsModal
                    isOpen={commentsModalOpen}
                    onClose={() => {
                        setCommentsModalOpen(false);
                        setSelectedSessionForComments(null);
                        setSelectedSessionData(null);
                    }}
                    module="session"
                    objectId={selectedSessionForComments}
                    moduleName={t('interviewOverview.comments', 'Interview Session')}
                    objectName={selectedSessionData
                        ? `${t('candidateDetails.interviewSessions.interviewId', { id: selectedSessionForComments })}${selectedSessionData?.job_title ? ` - ${selectedSessionData.job_title}` : ''}`
                        : `${t('candidateDetails.interviewSessions.interviewId', { id: selectedSessionForComments })}`
                    }
                    objectDetails={selectedSessionData}
                />
            )}

            {/* Archive/Unarchive Confirmation Modal */}
            <DynamicBasicDialog
                open={archiveDialogOpen}
                onClose={() => {
                    setArchiveDialogOpen(false);
                    setSessionToArchive(null);
                }}
                onConfirm={handleConfirmArchive}
                title={sessionToArchive?.is_archived
                    ? t('interviewOverview.unarchiveConfirmation', 'Unarchive Confirmation')
                    : t('candidateDetails.archiveConfirmation', 'Archive Confirmation')
                }
                content={sessionToArchive?.is_archived
                    ? t('interviewOverview.unarchiveSessionMessage', 'Are you sure you want to unarchive this interview session?')
                    : t('candidateDetails.archiveSessionMessage', 'Are you sure you want to archive this interview session?')
                }
                confirmButtonText={sessionToArchive?.is_archived
                    ? t('interviewOverview.unarchive', 'Unarchive')
                    : t('interviewOverview.archive', 'Archive')
                }
            />

            {/* Post-Interview Feedback Modal */}
            <FeedbackModal
                isOpen={feedbackModalOpen}
                onClose={() => {
                    setFeedbackModalOpen(false);
                    setFeedbackModalData(null);
                    setFeedbackCandidateInfo(null);
                    setFeedbackModalError('');
                }}
                feedbackData={feedbackModalData}
                loading={feedbackModalLoading}
                error={feedbackModalError}
                candidateInfo={feedbackCandidateInfo}
            />
        </div>
    );
}
